import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { loadAuditLog, fNum } from '../db/company';
import { selectedJobAtom, selectedVersionAtom, dataSourceVersionAtom } from '../store/company';
import { DAY_ZERO_AUDIT_TAB, groupBudgetEvents, attachBudgetDiffs } from '../helpers/dayZeroHistory';
import moment from 'moment';

// The server caps a page at 1000 rows. One upload is a few hundred rows, so a
// page holds two or three uploads; the panel keeps paging until the job's tab
// history is exhausted or this many pages are in hand, then offers to go on.
const PAGE_LIMIT = 1000;
const PAGES_PER_LOAD = 8;

// Audit_Datetime is GETDATE() on a server that runs US Central, handed back
// mislabeled as UTC. Read the components as they are and label them CT; a
// .local() would shift every time by the Central offset a second time.
const fmtWhen = (d) => d ? moment.utc(d).format('MMM D, YYYY h:mm A') + ' CT' : '';

const versionLabel = (guid, versionsById) => {
    if (guid == null || guid === '') return '';
    const v = versionsById.get(String(guid));
    if (!v || !v.Version_Start_DateTime) return 'v' + guid;
    const wip = String(v.Version_Type || '').includes('WIP') ? ' WIP' : '';
    return moment(v.Version_Start_DateTime).format('MMM D, YYYY') + wip;
};

const money = (n) => (n < 0 ? '-$' : '$') + fNum(Math.abs(n), 1);
const signed = (n) => (n > 0 ? '+' : n < 0 ? '-' : '') + '$' + fNum(Math.abs(n), 1);
const deltaColor = (n) => n > 0 ? 'var(--success)' : n < 0 ? 'var(--destructive)' : 'var(--text-secondary)';

const COLS = [
    { key: 'when', label: 'When', w: 180 },
    { key: 'who', label: 'Who', w: 200 },
    { key: 'version', label: 'Version', w: 130 },
    { key: 'what', label: 'What', w: 170 },
    { key: 'cost', label: 'Cost budget', w: 120, num: true },
    { key: 'revenue', label: 'Revenue', w: 120, num: true },
    { key: 'change', label: 'Change vs previous upload', w: 300 },
];

const th = (c) => (
    <Text key={c.key} style={{ width: c.w, fontSize: 12, fontWeight: '500', color: 'var(--muted-foreground)', paddingRight: 8, textAlign: c.num ? 'right' : 'left' }}>
        {c.label}
    </Text>
);
const td = (text, c, extra) => (
    <Text style={{ width: c.w, fontSize: 13, color: 'var(--foreground)', paddingRight: 8, textAlign: c.num ? 'right' : 'left', fontVariantNumeric: 'tabular-nums', ...(extra || {}) }}>
        {text}
    </Text>
);

const changeSummary = (e) => {
    if (e.kind === 'edit') return `${e.changes.length} field${e.changes.length === 1 ? '' : 's'} edited`;
    if (e.partial) return 'Older rows not loaded; this upload may be incomplete';
    if (!e.diff) return 'Previous upload not loaded';
    if (e.diff.first) return 'First upload on this version';
    const parts = [];
    if (e.diff.added.length) parts.push(`${e.diff.added.length} added`);
    if (e.diff.removed.length) parts.push(`${e.diff.removed.length} removed`);
    if (e.diff.changed.length) parts.push(`${e.diff.changed.length} changed`);
    if (!parts.length) return 'Same lines and amounts as the previous upload';
    return `Cost ${signed(e.diff.costDelta)}, revenue ${signed(e.diff.revenueDelta)}: ${parts.join(', ')}`;
};

const LINE_COLS = [
    { key: 'code', label: 'Cost code', w: 130 },
    { key: 'type', label: 'Cost type', w: 90 },
    { key: 'before', label: 'Before', w: 120, num: true },
    { key: 'after', label: 'After', w: 120, num: true },
    { key: 'delta', label: 'Change', w: 120, num: true },
    { key: 'hours', label: 'Hours', w: 160 },
];

const LineTable = ({ title, lines }) => {
    if (!lines.length) return null;
    const shown = lines.slice(0, 200);
    return (
        <View style={{ marginTop: 10 }}>
            <Text style={{ fontSize: 13, fontWeight: '600', color: 'var(--foreground)', marginBottom: 4 }}>{title} ({lines.length})</Text>
            <View style={{ flexDirection: 'row', backgroundColor: 'var(--subtle)', borderBottom: '1px solid var(--border)', padding: '6px 10px' }}>
                {LINE_COLS.map(th)}
            </View>
            {shown.map((l) => {
                const hoursText = l.hoursBefore !== undefined && Math.abs(l.hoursAfter - l.hoursBefore) >= 0.005
                    ? `${fNum(l.hoursBefore, 1)} → ${fNum(l.hoursAfter, 1)}`
                    : l.hours ? fNum(l.hours, 1) : '';
                return (
                    <View key={l.key} style={{ flexDirection: 'row', padding: '5px 10px', borderBottom: '1px solid var(--border)' }}>
                        {td(l.code, LINE_COLS[0])}
                        {td(l.type, LINE_COLS[1])}
                        {td(money(l.before), LINE_COLS[2], { color: 'var(--text-secondary)' })}
                        {td(money(l.after), LINE_COLS[3])}
                        {td(signed(l.delta), LINE_COLS[4], { color: deltaColor(l.delta), fontWeight: '500' })}
                        {td(hoursText, LINE_COLS[5], { color: 'var(--text-secondary)' })}
                    </View>
                );
            })}
            {lines.length > shown.length ? (
                <Text style={{ fontSize: 12, color: 'var(--muted-foreground)', padding: '6px 10px' }}>Showing the {shown.length} largest of {lines.length}.</Text>
            ) : null}
        </View>
    );
};

const EditTable = ({ changes }) => (
    <View style={{ marginTop: 10 }}>
        <View style={{ flexDirection: 'row', backgroundColor: 'var(--subtle)', borderBottom: '1px solid var(--border)', padding: '6px 10px' }}>
            {[{ key: 'a', label: 'Action', w: 90 }, { key: 'c', label: 'Cost code', w: 130 }, { key: 't', label: 'Cost type', w: 90 }, { key: 'f', label: 'Field', w: 140 }, { key: 'p', label: 'Before', w: 160 }, { key: 'n', label: 'After', w: 160 }].map(th)}
        </View>
        {changes.map((c) => (
            <View key={c.id} style={{ flexDirection: 'row', padding: '5px 10px', borderBottom: '1px solid var(--border)' }}>
                {td(c.action === 'DELETE' ? 'Deleted' : 'Edited', { w: 90 }, { color: c.action === 'DELETE' ? 'var(--destructive-text)' : 'var(--foreground)' })}
                {td(c.code, { w: 130 })}
                {td(c.type, { w: 90 })}
                {td(c.field, { w: 140 })}
                {td(c.prev == null ? '' : String(c.prev), { w: 160 }, { color: 'var(--text-secondary)' })}
                {td(c.next == null ? '' : String(c.next), { w: 160 })}
            </View>
        ))}
    </View>
);

/**
 * Collapsed-by-default history of Day Zero budget uploads for the selected job:
 * one row per upload (or per burst of hand edits), with the change against the
 * previous upload on the same version, and the changed lines on expand.
 */
const DayZeroHistory = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [versions] = useAtom(dataSourceVersionAtom);

    const [open, setOpen] = useState(false);
    const [onlyThisVersion, setOnlyThisVersion] = useState(false);
    const [rawRows, setRawRows] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    // A response that lands after the job changed belongs to the old job.
    const generationRef = useRef(0);
    const cursorRef = useRef(null);

    const fetchRef = useRef();
    fetchRef.current = async (append = false) => {
        if (!selectedJob) return;
        if (!append) { generationRef.current += 1; cursorRef.current = null; }
        const generation = generationRef.current;
        const job = selectedJob;
        setLoading(true);
        setError(null);
        const got = [];
        let more = false;
        try {
            for (let page = 0; page < PAGES_PER_LOAD; page++) {
                const res = await loadAuditLog({ buId: job, tab: DAY_ZERO_AUDIT_TAB, beforeId: cursorRef.current, limit: PAGE_LIMIT });
                if (res == null) throw new Error('Could not reach the audit log service.');
                if (res.error) throw new Error(res.error);
                const rows = res.data || [];
                got.push(...rows);
                if (rows.length > 0) cursorRef.current = rows[rows.length - 1].Audit_ID;
                more = rows.length >= PAGE_LIMIT;
                if (!more) break;
            }
        } catch (err) {
            if (generation !== generationRef.current) return;
            setError(String(err?.message || err));
            if (!append) setRawRows([]);
            setLoading(false);
            return;
        }
        if (generation !== generationRef.current) return;
        setRawRows((prev) => append ? [...prev, ...got] : got);
        setTruncated(more);
        setLoading(false);
    };
    const fetchPage = useCallback((append = false) => fetchRef.current && fetchRef.current(append), []);

    useEffect(() => {
        if (!open) return;
        if (!selectedJob) { setRawRows([]); return; }
        fetchPage(false);
    }, [open, selectedJob, fetchPage]);

    const versionsById = new Map((Array.isArray(versions) ? versions : []).map((v) => [String(v.Version_GUID), v]));

    // Diffs are computed over every version so "previous upload" is right, and
    // the version filter only hides rows afterwards.
    const allEvents = attachBudgetDiffs(groupBudgetEvents(rawRows), { truncated });
    const events = onlyThisVersion ? allEvents.filter((e) => String(e.version) === String(selectedVersion)) : allEvents;
    const uploads = events.filter((e) => e.kind === 'upload').length;

    const chip = (label, on, onPress) => (
        <Pressable
            onPress={onPress}
            aria-pressed={on}
            style={{ padding: '3px 10px', borderRadius: 'var(--radius-inner)', backgroundColor: on ? 'var(--primary)' : 'transparent', cursor: 'pointer' }}
        >
            <Text style={{ fontSize: 12, fontWeight: '500', color: on ? 'var(--primary-foreground)' : 'var(--muted-foreground)' }}>{label}</Text>
        </Pressable>
    );

    return (
        <View style={{ marginBottom: 16, backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-xs)', overflow: 'hidden' }}>
            <Pressable
                data-test="day-zero-history-toggle"
                onPress={() => setOpen((v) => !v)}
                style={({ hovered }) => ({ flexDirection: 'row', alignItems: 'center', gap: 8, padding: '10px 16px', cursor: 'pointer', backgroundColor: hovered ? 'var(--muted)' : 'var(--card)' })}
            >
                <Text style={{ fontSize: 11, color: 'var(--muted-foreground)', width: 10 }}>{open ? '▾' : '▸'}</Text>
                <Text style={{ fontSize: 15, fontWeight: '600', color: 'var(--foreground)' }}>Budget upload history</Text>
                {open && !loading && !error ? (
                    <Text style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                        {uploads} upload{uploads === 1 ? '' : 's'}{events.length - uploads ? `, ${events.length - uploads} edit${events.length - uploads === 1 ? '' : 's'}` : ''}{truncated ? ' loaded so far' : ''}
                    </Text>
                ) : null}
                {loading ? <Text style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Loading…</Text> : null}
            </Pressable>

            {open ? (
                <View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: '8px 16px', borderTop: '1px solid var(--border)' }}>
                        <View style={{ flexDirection: 'row', gap: 2, padding: 3, backgroundColor: 'var(--muted)', borderRadius: 'var(--radius)' }}>
                            {chip('All versions', !onlyThisVersion, () => setOnlyThisVersion(false))}
                            {chip('This version', onlyThisVersion, () => setOnlyThisVersion(true))}
                        </View>
                        <Pressable onPress={() => fetchPage(false)} style={({ hovered }) => ({ height: 28, padding: '0 10px', justifyContent: 'center', borderRadius: 'var(--radius)', border: '1px solid var(--border)', backgroundColor: hovered ? 'var(--muted)' : 'var(--background)', cursor: 'pointer' })}>
                            <Text style={{ fontSize: 12, fontWeight: '500', color: 'var(--foreground)' }}>Refresh</Text>
                        </Pressable>
                        {truncated ? (
                            <Pressable onPress={() => fetchPage(true)} style={({ hovered }) => ({ height: 28, padding: '0 10px', justifyContent: 'center', borderRadius: 'var(--radius)', backgroundColor: hovered ? 'var(--primary-hover)' : 'var(--primary)', cursor: loading ? 'wait' : 'pointer' })}>
                                <Text style={{ fontSize: 12, fontWeight: '500', color: 'var(--primary-foreground)' }}>Load older uploads</Text>
                            </Pressable>
                        ) : null}
                        <Text style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Each row is one upload of the Day Zero budget. Click a row for the lines that changed.</Text>
                    </View>

                    {error ? <Text style={{ fontSize: 13, color: 'var(--destructive-text)', padding: '8px 16px' }}>Error: {error}</Text> : null}

                    {!error && !loading && events.length === 0 ? (
                        <Text style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 16px 14px' }}>
                            {selectedJob
                                ? (onlyThisVersion && allEvents.length ? 'No uploads on this version. Switch to All versions to see the rest.' : 'No budget uploads recorded for this job. Uploads before the change log went live are not listed.')
                                : 'Select a job to see its upload history.'}
                        </Text>
                    ) : null}

                    {events.length > 0 ? (
                        <View style={{ overflowX: 'auto' }}>
                            <View style={{ flexDirection: 'row', backgroundColor: 'var(--subtle)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '8px 16px' }}>
                                {COLS.map(th)}
                            </View>
                            {events.map((e) => {
                                const isOpen = expandedId === e.id;
                                const isUpload = e.kind === 'upload';
                                return (
                                    <React.Fragment key={e.id}>
                                        <Pressable
                                            onPress={() => setExpandedId(isOpen ? null : e.id)}
                                            aria-selected={isOpen}
                                            style={({ hovered }) => ({ flexDirection: 'row', alignItems: 'center', padding: '7px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', backgroundColor: isOpen || hovered ? 'var(--muted)' : 'var(--card)', boxShadow: isOpen ? 'inset 3px 0 0 var(--destructive)' : 'none' })}
                                        >
                                            {td(fmtWhen(e.when), COLS[0])}
                                            {td(e.userName || e.user, COLS[1])}
                                            {td(versionLabel(e.version, versionsById), COLS[2], { color: 'var(--text-secondary)' })}
                                            {td(isUpload ? `Upload, ${e.lineCount} line${e.lineCount === 1 ? '' : 's'}` : `Hand edit, ${e.changes.length} change${e.changes.length === 1 ? '' : 's'}`, COLS[3])}
                                            {td(isUpload ? money(e.totals.cost) : '', COLS[4])}
                                            {td(isUpload ? money(e.totals.revenue) : '', COLS[5])}
                                            {td(changeSummary(e), COLS[6], { color: e.diff && !e.diff.first ? deltaColor(e.diff.costDelta) : 'var(--text-secondary)' })}
                                        </Pressable>
                                        {isOpen ? (
                                            <View style={{ padding: '6px 16px 14px 32px', borderBottom: '1px solid var(--border)', backgroundColor: 'var(--subtle)' }}>
                                                {!isUpload ? <EditTable changes={e.changes} /> : null}
                                                {isUpload && e.diff && e.diff.first ? (
                                                    <LineTable title="Lines in this upload" lines={e.diff.added} />
                                                ) : null}
                                                {isUpload && e.diff && !e.diff.first ? (
                                                    <>
                                                        <Text style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                                            Compared with the previous upload on this version. Cost {signed(e.diff.costDelta)}, revenue {signed(e.diff.revenueDelta)}, hours {e.diff.hoursDelta > 0 ? '+' : ''}{fNum(e.diff.hoursDelta, 1)}.
                                                        </Text>
                                                        {!e.diff.added.length && !e.diff.removed.length && !e.diff.changed.length ? (
                                                            <Text style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>No line changed.</Text>
                                                        ) : null}
                                                        <LineTable title="Changed" lines={e.diff.changed} />
                                                        <LineTable title="Added" lines={e.diff.added} />
                                                        <LineTable title="Removed" lines={e.diff.removed} />
                                                    </>
                                                ) : null}
                                                {isUpload && !e.diff ? (
                                                    <Text style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Load older uploads to compare this one with the upload before it.</Text>
                                                ) : null}
                                            </View>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                        </View>
                    ) : null}
                </View>
            ) : null}
        </View>
    );
};

export default DayZeroHistory;
